import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/datetime';
import type { ExamSchedule, LessonLog, TestRecord } from '@/types';
import type { RecentLesson } from '@/pages/dashboard/components/RecentLessonsCard';
import type { UpcomingExam } from '@/pages/dashboard/components/UpcomingExamsCard';

interface StudentOption {
  id: string;
  name: string;
}

interface MonthlyReportModalProps {
  open: boolean;
  onClose: () => void;
  teacherName: string;
  students: StudentOption[];
}

function todayMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthRange(month: string): { start: string; end: string } {
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return {
    start: `${month}-01`,
    end: `${month}-${String(lastDay).padStart(2, '0')}`,
  };
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `${y}년 ${Number(m)}월`;
}

export default function MonthlyReportModal({
  open,
  onClose,
  teacherName,
  students,
}: MonthlyReportModalProps) {
  const [month, setMonth] = useState<string>(todayMonth());
  const [studentId, setStudentId] = useState<string>('');
  const [lessons, setLessons] = useState<RecentLesson[]>([]);
  const [exams, setExams] = useState<UpcomingExam[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open && !studentId && students.length > 0) {
      setStudentId(students[0].id);
    }
  }, [open, studentId, students]);

  useEffect(() => {
    if (!open) return;
    if (!studentId) {
      setLessons([]);
      setExams([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const { start, end } = monthRange(month);
        const nameMap = new Map<string, string>();
        students.forEach((s) => nameMap.set(s.id, s.name));

        const [lessonsRes, examsRes] = await Promise.all([
          supabase
            .from('lesson_logs')
            .select('*')
            .eq('student_id', studentId)
            .gte('lesson_date', start)
            .lte('lesson_date', end)
            .order('lesson_date', { ascending: true }),
          supabase
            .from('exam_schedules')
            .select('*')
            .eq('student_id', studentId)
            .gte('start_date', start)
            .lte('start_date', end)
            .order('start_date', { ascending: true }),
        ]);
        if (lessonsRes.error) throw lessonsRes.error;

        const rows = (lessonsRes.data as LessonLog[]) ?? [];
        const lessonIds = rows.map((l) => l.id);
        const testsByLesson = new Map<string, TestRecord[]>();
        if (lessonIds.length > 0) {
          const testsRes = await supabase
            .from('test_records')
            .select('*')
            .in('lesson_log_id', lessonIds);
          ((testsRes.data as TestRecord[]) ?? []).forEach((t) => {
            if (!t.lesson_log_id) return;
            const list = testsByLesson.get(t.lesson_log_id) ?? [];
            list.push(t);
            testsByLesson.set(t.lesson_log_id, list);
          });
        }

        if (cancelled) return;
        setLessons(
          rows.map((l) => ({
            ...l,
            studentName: nameMap.get(l.student_id) ?? '학생',
            tests: testsByLesson.get(l.id) ?? [],
          })),
        );
        setExams(
          ((examsRes.data as ExamSchedule[]) ?? []).map((e) => ({
            ...e,
            studentName: nameMap.get(e.student_id) ?? '학생',
          })),
        );
      } catch {
        if (!cancelled) setError('월간 리포트 데이터를 불러오지 못했어요.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, month, studentId, students]);

  const stats = useMemo(() => {
    const present = lessons.filter((l) => !l.is_absent).length;
    const absent = lessons.filter((l) => l.is_absent).length;
    const testCount = lessons.reduce((acc, l) => acc + l.tests.length, 0);
    return [
      { label: '수업 기록', value: `${lessons.length}건` },
      { label: '출석 / 결석', value: `${present} / ${absent}` },
      { label: '테스트 기록', value: `${testCount}건` },
      { label: '시험 일정', value: `${exams.length}건` },
    ];
  }, [lessons, exams]);

  const selectedName = students.find((s) => s.id === studentId)?.name ?? '학생';

  if (!open) return null;

  return createPortal(
    <div className="report-overlay fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 md:p-8">
      <div className="report-panel my-auto w-full max-w-3xl rounded-xl border border-background-200 bg-background-50">
        <div className="no-print flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-5 py-4 md:px-6">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-file-chart-line"></i>
            </span>
            <h2 className="text-base font-bold text-foreground-950">월간 리포트 미리보기</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1.5">
              <i className="ri-user-line text-foreground-500"></i>
              <select
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className="max-w-[150px] bg-transparent text-sm text-foreground-800 focus:outline-none"
              >
                {students.length === 0 && <option value="">학생 없음</option>}
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1.5">
              <i className="ri-calendar-line text-foreground-500"></i>
              <input
                type="month"
                value={month}
                onChange={(e) => e.target.value && setMonth(e.target.value)}
                className="bg-transparent text-sm text-foreground-800 focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-background-300 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-printer-line"></i>
              인쇄 · PDF 저장
            </button>
          </div>
        </div>

        <div className="report-scroll max-h-[75vh] overflow-y-auto p-5 md:p-8">
          <div className="[-webkit-print-color-adjust:exact] [print-color-adjust:exact]">
            <header className="flex flex-wrap items-start justify-between gap-3 border-b-2 border-foreground-900 pb-4">
              <div>
                <p className="text-sm font-semibold text-primary-700">월간 학습 리포트</p>
                <h1 className="mt-1 text-2xl font-bold text-foreground-950">
                  {teacherName} 선생님
                </h1>
                <p className="mt-1 text-sm text-foreground-600">
                  {selectedName} · 기간 {monthLabel(month)}
                </p>
              </div>
              <div className="text-right text-xs text-foreground-500">
                <p>발행일 {formatDate(new Date().toISOString())}</p>
                <p className="mt-0.5">총 {lessons.length}건 수업 기록</p>
              </div>
            </header>

            {error ? (
              <p className="mt-6 rounded-lg border border-dashed border-background-300 px-4 py-10 text-center text-sm text-foreground-500">
                {error}
              </p>
            ) : loading ? (
              <div className="mt-6 flex items-center justify-center py-16 text-sm text-foreground-500">
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                불러오는 중...
              </div>
            ) : (
              <>
                <section className="report-section mt-6">
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {stats.map((s) => (
                      <div key={s.label} className="rounded-lg border border-background-300 bg-background-100 p-3">
                        <p className="text-xs text-foreground-500">{s.label}</p>
                        <p className="mt-1 text-lg font-bold text-foreground-950">{s.value}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="report-section mt-8">
                  <h3 className="flex items-center gap-2 border-l-4 border-accent-500 pl-2.5 text-base font-bold text-foreground-950">
                    이 달의 수업일지
                  </h3>
                  {lessons.length === 0 ? (
                    <p className="mt-3 rounded-lg border border-dashed border-background-300 px-4 py-6 text-center text-sm text-foreground-500">
                      이 달에 기록된 수업일지가 없어요.
                    </p>
                  ) : (
                    <table className="mt-3 w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-background-200 text-left text-xs text-foreground-700">
                          <th className="border border-background-300 px-3 py-2 font-semibold">날짜</th>
                          <th className="border border-background-300 px-3 py-2 font-semibold">수업 범위</th>
                          <th className="border border-background-300 px-3 py-2 font-semibold">다음 숙제</th>
                          <th className="border border-background-300 px-3 py-2 font-semibold">테스트</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lessons.map((l) => (
                          <tr key={l.id}>
                            <td className="border border-background-300 px-3 py-2 whitespace-nowrap text-foreground-600">
                              {formatDate(l.lesson_date)}
                              {l.is_absent && (
                                <span className="ml-1 text-[11px] text-accent-700">결석</span>
                              )}
                            </td>
                            <td className="border border-background-300 px-3 py-2 text-foreground-700">
                              {l.lesson_scope || '-'}
                            </td>
                            <td className="border border-background-300 px-3 py-2 text-foreground-700">
                              {l.next_homework || '-'}
                            </td>
                            <td className="border border-background-300 px-3 py-2 text-foreground-700">
                              {l.tests.length === 0
                                ? '-'
                                : l.tests
                                    .map(
                                      (t) =>
                                        `${t.test_scope || '테스트'}${t.difficulty ? ` (난이도 ${t.difficulty})` : ''}${t.score ? ` ${t.score}` : ''}`,
                                    )
                                    .join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>

                <section className="report-section mt-8">
                  <h3 className="flex items-center gap-2 border-l-4 border-secondary-500 pl-2.5 text-base font-bold text-foreground-950">
                    이 달의 시험 일정
                  </h3>
                  {exams.length === 0 ? (
                    <p className="mt-3 rounded-lg border border-dashed border-background-300 px-4 py-6 text-center text-sm text-foreground-500">
                      이 달에 예정된 시험이 없어요.
                    </p>
                  ) : (
                    <table className="mt-3 w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-background-200 text-left text-xs text-foreground-700">
                          <th className="border border-background-300 px-3 py-2 font-semibold">시작일</th>
                          <th className="border border-background-300 px-3 py-2 font-semibold">시험</th>
                          <th className="border border-background-300 px-3 py-2 font-semibold">범위</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exams.map((e) => (
                          <tr key={e.id}>
                            <td className="border border-background-300 px-3 py-2 whitespace-nowrap text-foreground-600">
                              {e.start_date ?? '-'}
                            </td>
                            <td className="border border-background-300 px-3 py-2 text-foreground-700">
                              {e.title || '시험'}
                            </td>
                            <td className="border border-background-300 px-3 py-2 text-foreground-700">
                              {e.scope || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              </>
            )}

            <footer className="mt-10 border-t border-background-300 pt-3 text-center text-xs text-foreground-400">
              본 리포트는 과외매니저에서 자동 생성되었습니다.
            </footer>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}